import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import {
  InfiniteData,
  keepPreviousData,
  QueryKey,
  UseInfiniteQueryResult,
  useInfiniteQuery as useReactQueryInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useCallback, useMemo, useState } from 'react';

interface PageData {
  total: number;
  currentPage: number;
  perPage: number;
  lastPage: number;
}

interface WithPageData {
  pageData: PageData;
}

type ArrayElement<T> = T extends Array<infer U> ? U : never;

type InfiniteQueryResult<R, K extends keyof R> = {
  [P in K]: R[P];
} & {
  total: number;
  fetchNextPage: () => Promise<void>;
  loadAll: () => Promise<void>;
  isLoading: boolean;
  unloadedContentCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  paginationModel: GridPaginationModel;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
  rawResult: UseInfiniteQueryResult<InfiniteData<AxiosResponse<R, unknown>, unknown>, Error>;
  error?: AxiosError;
  mutateItem: <T extends ArrayElement<R[K]>>(
    predicate: (item: ArrayElement<R[K]>) => boolean,
    updater: (item: ArrayElement<R[K]>) => T
  ) => void;
  refetchMatchingPages: (predicate: (item: ArrayElement<R[K]>) => boolean) => Promise<void>;
};

export function useInfiniteQuery<
  P extends { page?: number; perPage?: number },
  R extends WithPageData,
  K extends keyof R = keyof R,
>({
  queryKey,
  queryFunction,
  queryParams,
  options,
  dataKey = 'contents' as K,
}: {
  queryKey: QueryKey | ((parameters: P) => QueryKey);
  queryFunction: (parameters: P) => Promise<AxiosResponse<R>>;
  queryParams?: P;
  options?: {
    page?: number;
    perPage?: number;
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    refetchOnMount?: boolean;
  };
  dataKey?: K;
}): InfiniteQueryResult<R, K> {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: options?.page ?? 0,
    pageSize: options?.perPage ?? DATA_GRID_LAZY_PAGE_SIZE,
  });
  const queryClient = useQueryClient();

  const result = useReactQueryInfiniteQuery({
    queryKey: typeof queryKey === 'function' ? queryKey(queryParams ?? ({} as P)) : queryKey,
    queryFn: ({ pageParam: pageParameter = 1 }) =>
      queryFunction({
        ...queryParams,
        page: pageParameter,
        perPage: paginationModel.pageSize,
      } as P),
    getNextPageParam: lastPage => {
      const pageData = lastPage.data.pageData;
      if (!pageData || typeof pageData !== 'object') return;
      const { currentPage, lastPage: totalPages } = pageData;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 1,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    refetchOnMount: options?.refetchOnMount ?? false,
    refetchOnReconnect: false,
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
  });

  const pages = result.data?.pages;

  const data: R[K] = useMemo(
    () => (pages ?? []).flatMap(page => (page.data[dataKey] ?? []) as ArrayElement<R[K]>[]) as R[K],
    [pages, dataKey]
  );
  const total = pages?.[0]?.data.pageData.total ?? 0;

  const { hasNextPage, fetchNextPage: fetchNextQueryPage } = result;

  const fetchNextPage = useCallback(async () => {
    if (hasNextPage) {
      await fetchNextQueryPage();
    }
  }, [hasNextPage, fetchNextQueryPage]);

  const loadAll = async () => {
    while (result.hasNextPage) {
      await result.fetchNextPage();
    }
  };

  const mutateItem = <T extends ArrayElement<R[K]>>(
    predicate: (item: ArrayElement<R[K]>) => boolean,
    updater: (item: ArrayElement<R[K]>) => T
  ) => {
    const currentQueryKey = typeof queryKey === 'function' ? queryKey(queryParams ?? ({} as P)) : queryKey;

    queryClient.setQueryData<InfiniteData<AxiosResponse<R, unknown>, unknown>>(currentQueryKey, oldData => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          data: {
            ...page.data,
            [dataKey]: (page.data[dataKey] as ArrayElement<R[K]>[]).map(item =>
              predicate(item) ? updater(item) : item
            ),
          },
        })),
      };
    });
  };

  const refetchMatchingPages = async (predicate: (item: ArrayElement<R[K]>) => boolean): Promise<void> => {
    const currentQueryKey = typeof queryKey === 'function' ? queryKey(queryParams ?? ({} as P)) : queryKey;
    const existing = queryClient.getQueryData<InfiniteData<AxiosResponse<R, unknown>, unknown>>(currentQueryKey);
    if (!existing) return;

    const parametersToRefetch = existing.pages
      .map((page, index) => ({ pageParam: existing.pageParams[index], page }))
      .filter(({ page }) => ((page.data[dataKey] ?? []) as ArrayElement<R[K]>[]).some(item => predicate(item)))
      .map(({ pageParam }) => pageParam);
    if (parametersToRefetch.length === 0) return;

    const refreshed = await Promise.all(
      parametersToRefetch.map(async pageParameter => ({
        pageParam: pageParameter,
        response: await queryFunction({
          ...queryParams,
          page: pageParameter as number,
          perPage: paginationModel.pageSize,
        } as P),
      }))
    );
    const responseByParameter = new Map(refreshed.map(({ pageParam, response }) => [pageParam, response]));

    queryClient.setQueryData<InfiniteData<AxiosResponse<R, unknown>, unknown>>(currentQueryKey, oldData => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page, index) => responseByParameter.get(oldData.pageParams[index]) ?? page),
      };
    });
  };

  return {
    [dataKey]: data,
    total,
    fetchNextPage,
    loadAll,
    isLoading: result.isLoading,
    unloadedContentCount: total - (Array.isArray(data) ? data.length : 0),
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    paginationModel,
    setPaginationModel,
    rawResult: result,
    error: result.error,
    mutateItem,
    refetchMatchingPages,
  } as InfiniteQueryResult<R, K>;
}
